import React, { useState } from "react";
import {
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  Box,
  Grid,
  TextField,
  MenuItem,
  TablePagination,
  Skeleton,
} from "@material-ui/core";
import { DatePicker, LocalizationProvider } from "@material-ui/lab";
import AdapterDateFns from "@material-ui/lab/AdapterDateFns";
import { es } from "date-fns/locale";
import VisibilityIcon from "@material-ui/icons/Visibility";
import { TablaHead } from "src/components/_dashboard/tablas";
import Scrollbar from "src/components/Scrollbar";
import SearchNotFound from "src/components/SearchNotFound";
import { useObtenerLotesQuery } from "src/services/conciliacionApi";
import {
  getBancoConfig,
  getOpcionesBancos,
  formatearPeriodoLote,
} from "src/utils/conciliacionHelpers";
import dayjs from "dayjs";
import ModalProcesarAutomatico from "./ModalProcesarAutomatico";
import ModalResultadosProcesamiento from "./ModalResultadosProcesamiento";
import { FlashOn } from "@material-ui/icons";
import SugerenciasModal from "./SugerenciasModal";
import ConciliacionManualModal from "./ConciliacionManualModal";
import { formatearFechaDisplay } from "src/utils/formatTime";

const TABLE_HEAD = [
  { id: "banco", label: "Banco", alignRight: false },
  { id: "archivo", label: "Archivo", alignRight: false },
  { id: "periodo", label: "Período", alignRight: false },
  { id: "created_at", label: "Fecha Carga", alignRight: false },
  { id: "total_movimientos", label: "Total", alignRight: true },
  { id: "ignorados", label: "Ignorados", alignRight: true },
  { id: "pendientes", label: "Pendientes", alignRight: true },
  { id: "conciliados", label: "Conciliados", alignRight: true },
  { id: "acciones", label: "Acciones", alignRight: true },
];
export default function LotesTable({ onVerMovimientos }) {
  // Paginación
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Filtros
  const [filtros, setFiltros] = useState({
    banco: "",
    fechaDesde: null,
    fechaHasta: null,
  });

  // Estados para modales de procesamiento
  const [modalProcesar, setModalProcesar] = useState({
    open: false,
    loteId: null,
  });
  const [modalResultados, setModalResultados] = useState({
    open: false,
    resultados: null,
  });

  const [openSugerencias, setOpenSugerencias] = useState({
    open: false,
    movimiento: null,
  });
  const [openConciliar, setOpenConciliar] = useState({
    open: false,
    movimiento: null,
  });

  // Query de RTK
  const { data, isLoading, isFetching } = useObtenerLotesQuery({
    banco: filtros.banco || undefined,
    fecha_desde: filtros.fechaDesde
      ? dayjs(filtros.fechaDesde).format("YYYY-MM-DD")
      : undefined,
    fecha_hasta: filtros.fechaHasta
      ? dayjs(filtros.fechaHasta).format("YYYY-MM-DD")
      : undefined,
    page: page + 1, // Backend usa página 1-indexed
    limit: rowsPerPage,
  });

  // Handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFiltroChange = (campo, valor) => {
    setFiltros((prev) => ({ ...prev, [campo]: valor }));
    setPage(0); // Reset a primera página al filtrar
  };

  // Handler para abrir modal de procesamiento
  const handleProcesarLote = (loteId) => {
    setModalProcesar({
      open: true,
      loteId: loteId,
    });
  };

  // Handler cuando termina el procesamiento
  const handleSuccessProcesamiento = (resultados) => {
    // Cerrar modal de confirmación
    setModalProcesar({ open: false, loteId: null });

    // Abrir modal de resultados
    setModalResultados({
      open: true,
      resultados: resultados,
    });
  };

  // Datos procesados
  const lotes = data?.data || [];
  const totalLotes = data?.pagination?.total || 0;
  const isNotFound = !isLoading && lotes.length === 0;

  return (
    <Card>
      {/* SECCIÓN DE FILTROS */}
      <Box sx={{ p: 3 }}>
        <Grid container spacing={2}>
          {/* Filtro: Banco */}
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              select
              label="Banco"
              value={filtros.banco}
              onChange={(e) => handleFiltroChange("banco", e.target.value)}
              size="small"
            >
              {getOpcionesBancos().map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          {/* Filtro: Fecha Desde */}
          <Grid item xs={12} sm={4}>
            <LocalizationProvider dateAdapter={AdapterDateFns} locale={es}>
              <DatePicker
                label="Desde"
                value={filtros.fechaDesde}
                onChange={(newValue) =>
                  handleFiltroChange("fechaDesde", newValue)
                }
                renderInput={(params) => (
                  <TextField {...params} fullWidth size="small" />
                )}
                inputFormat="dd/MM/yyyy"
              />
            </LocalizationProvider>
          </Grid>

          {/* Filtro: Fecha Hasta */}
          <Grid item xs={12} sm={4}>
            <LocalizationProvider dateAdapter={AdapterDateFns} locale={es}>
              <DatePicker
                label="Hasta"
                value={filtros.fechaHasta}
                onChange={(newValue) =>
                  handleFiltroChange("fechaHasta", newValue)
                }
                renderInput={(params) => (
                  <TextField {...params} fullWidth size="small" />
                )}
                inputFormat="dd/MM/yyyy"
              />
            </LocalizationProvider>
          </Grid>
        </Grid>
      </Box>

      {/* TABLA */}
      <Scrollbar>
        <TableContainer sx={{ minWidth: 800 }}>
          <Table>
            <TablaHead headLabel={TABLE_HEAD} />
            <TableBody>
              {/* LOADING STATE */}
              {(isLoading || isFetching) && (
                <>
                  {[...Array(rowsPerPage)].map((_, index) => (
                    <TableRow key={index}>
                      {TABLE_HEAD.map((column) => (
                        <TableCell key={column.id}>
                          <Skeleton variant="text" />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </>
              )}

              {/* DATA ROWS */}
              {!isLoading &&
                !isFetching &&
                lotes.map((lote) => {
                  const bancoConfig = getBancoConfig(lote.banco);
                  const progreso = {
                    total: lote.total_movs || 0,
                    pendientes: lote.movs_pend || 0,
                    conciliados: lote.movs_conc || 0,
                    ignorados: lote.movs_ign || 0,
                  };

                  return (
                    <TableRow key={lote.id} hover>
                      {/* BANCO */}
                      <TableCell>
                        <Chip
                          label={bancoConfig.label}
                          size="small"
                          sx={{
                            backgroundColor: bancoConfig.bgColor,
                            color: bancoConfig.color,
                            fontWeight: 600,
                          }}
                        />
                      </TableCell>

                      {/* ARCHIVO */}
                      <TableCell>
                        <Tooltip title={lote.nombre_archivo}>
                          <Typography
                            variant="body2"
                            noWrap
                            sx={{ maxWidth: 200 }}
                          >
                            {lote.nombre_archivo}
                          </Typography>
                        </Tooltip>
                      </TableCell>

                      {/* PERÍODO */}
                      <TableCell>
                        <Typography variant="body2">
                          {formatearPeriodoLote(
                            lote.periodo_desde,
                            lote.periodo_hasta,
                          )}
                        </Typography>
                      </TableCell>

                      {/* FECHA CARGA */}
                      <TableCell>
                        <Typography variant="body2">
                          {formatearFechaDisplay(lote.created_at, true)}
                        </Typography>
                      </TableCell>

                      {/* TOTAL MOVIMIENTOS */}
                      <TableCell align="right">
                        <Chip
                          label={progreso.total}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      </TableCell>

                      {/* IGNORADOS */}
                      <TableCell align="right">
                        <Chip
                          label={progreso.ignorados}
                          size="small"
                          color="warning"
                          variant={
                            progreso.ignorados > 0 ? "filled" : "outlined"
                          }
                        />
                      </TableCell>

                      {/* PENDIENTES */}
                      <TableCell align="right">
                        <Chip
                          label={progreso.pendientes}
                          size="small"
                          color="warning"
                          variant={
                            progreso.pendientes > 0 ? "filled" : "outlined"
                          }
                        />
                      </TableCell>

                      {/* CONCILIADOS */}
                      <TableCell align="right">
                        <Chip
                          label={progreso.conciliados}
                          size="small"
                          color="success"
                          variant={
                            progreso.conciliados > 0 ? "filled" : "outlined"
                          }
                        />
                      </TableCell>

                      {/* ACCIONES */}
                      <TableCell align="right">
                        <Box display="flex" justifyContent="flex-end" gap={0.5}>
                          {/* Botón Procesar Lote (solo si hay pendientes) */}
                          {progreso.pendientes > 0 && (
                            <Tooltip title="Procesar movimientos automáticamente">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => handleProcesarLote(lote.id)}
                              >
                                <FlashOn />
                              </IconButton>
                            </Tooltip>
                          )}

                          {/* Botón Ver Movimientos */}
                          <Tooltip title="Ver movimientos">
                            <IconButton
                              size="small"
                              color="default"
                              onClick={() => onVerMovimientos(lote.id)}
                            >
                              <VisibilityIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>
      </Scrollbar>

      {/* EMPTY STATE */}
      {isNotFound && (
        <Box sx={{ py: 10 }}>
          <SearchNotFound searchQuery="lotes" />
        </Box>
      )}

      {/* PAGINACIÓN */}
      <TablePagination
        rowsPerPageOptions={[5, 10, 25, 50]}
        component="div"
        count={totalLotes}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelRowsPerPage="Filas por página:"
        labelDisplayedRows={({ from, to, count }) =>
          `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`
        }
      />

      {/* MODAL PROCESAR AUTOMÁTICO */}
      {modalProcesar.open && (
        <ModalProcesarAutomatico
          open={modalProcesar.open}
          onClose={() => setModalProcesar({ open: false, loteId: null })}
          loteId={modalProcesar.loteId}
          onSuccess={handleSuccessProcesamiento}
        />
      )}

      {/* MODAL RESULTADOS PROCESAMIENTO */}
      {modalResultados.open && (
        <ModalResultadosProcesamiento
          open={modalResultados.open}
          onClose={() => setModalResultados({ open: false, resultados: null })}
          resultados={modalResultados.resultados}
        />
      )}

      {/* MODAL SUGERENCIAS */}
      <SugerenciasModal
        open={openSugerencias.open}
        onClose={() => setOpenSugerencias({ open: false, movimiento: null })}
        movimiento={openSugerencias.movimiento}
        onConciliarManual={(mov) => {
          setOpenSugerencias({ open: false, movimiento: null });
          setOpenConciliar({ open: true, movimiento: mov });
        }}
      />

      {/*  MODAL CONCILIACIÓN MANUAL */}
      <ConciliacionManualModal
        open={openConciliar.open}
        onClose={() => setOpenConciliar({ open: false, movimiento: null })}
        movimiento={openConciliar.movimiento}
        onSuccess={() => {
          setOpenConciliar({ open: false, movimiento: null });
        }}
      />
    </Card>
  );
}
